import React, { useState, useMemo, useCallback } from 'react';

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

import { useError } from '../contexts/ErrorContext';
import { fetchOtaAttempts, fetchOtaAttemptsDay } from '../services/reportsService';
import { ANALYTICS_TIMEZONE, wallTimeToInstant, formatInTimeZone } from '../utils/timezone';
import AnalyticsTimezoneNote from './AnalyticsTimezoneNote';
import './OtaAttemptsReport.css';

const today = new Date().toISOString().slice(0, 10);
const defaultStart = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
})();

// The backend derives one failure segment per normalized `error_reason`, so the
// outcome keys aren't known ahead of time — colors are assigned by rank instead.
const SUCCESS_COLOR = '#30d158';
const NO_OUTCOME_COLOR = '#ffc107';
const MUTED_COLOR = '#8e8e93';   // Unknown / Other — deliberately not eye-catching
const FAILURE_RAMP = [
  '#ff375f', // red
  '#ff9f0a', // orange
  '#bf5af2', // purple
  '#ff6482', // pink
  '#ffd60a', // yellow
  '#64d2ff', // cyan
  '#5e5ce6', // indigo
  '#ac8e68', // tan
];
const MUTED_LABELS = new Set(['Unknown', 'Other']);

// key → color, built once per response so segment colors stay stable across
// re-renders and match the legend.
const buildColorMap = (outcomes) => {
  const map = {};
  let failureIndex = 0;
  for (const o of outcomes) {
    if (o.kind === 'success') {
      map[o.key] = SUCCESS_COLOR;
    } else if (o.kind === 'no_outcome') {
      map[o.key] = NO_OUTCOME_COLOR;
    } else if (MUTED_LABELS.has(o.label)) {
      map[o.key] = MUTED_COLOR;
    } else {
      map[o.key] = FAILURE_RAMP[failureIndex % FAILURE_RAMP.length];
      failureIndex += 1;
    }
  }
  return map;
};

// A metadata value the event didn't carry. Older app builds omit the firmware
// params entirely (Android is being fixed), and a blank cell would read as "this
// device has none" rather than "we weren't told".
const Unknownable = ({ value }) =>
  value ? value : <span className="ota-unknown">UNKNOWN</span>;

const ChartTooltip = ({ active, payload, label, outcomes, colorMap }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  // With a segment per failure reason, listing every bucket would be mostly
  // zeros — show only what actually fired that day.
  const present = outcomes.filter(o => (row.byOutcome?.[o.key]?.attempts || 0) > 0);

  return (
    <div className="ota-chart-tooltip">
      <div className="ota-chart-tooltip-date">{label}</div>
      {present.map((o) => (
        <div key={o.key} className="ota-chart-tooltip-row">
          <span className="ota-chart-tooltip-swatch" style={{ background: colorMap[o.key] }} />
          <span className="ota-chart-tooltip-label">{o.label}</span>
          <span className="ota-chart-tooltip-value">
            {(row.byOutcome?.[o.key]?.attempts || 0).toLocaleString()}
          </span>
        </div>
      ))}
      <div className="ota-chart-tooltip-divider" />
      <div className="ota-chart-tooltip-row">
        <span className="ota-chart-tooltip-label">Total attempts</span>
        <span className="ota-chart-tooltip-value">{(row.total_attempts || 0).toLocaleString()}</span>
      </div>
    </div>
  );
};

const OtaAttemptsReport = () => {
  const { addError } = useError();
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [dayError, setDayError] = useState(null);

  const fetchData = async () => {
    if (!startDate || !endDate) return;
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before or equal to end date.');
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);
    setSelectedDay(null);
    setDayData(null);
    setDayError(null);

    try {
      const result = await fetchOtaAttempts(startDate, endDate, addError);
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to fetch OTA attempts data.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') fetchData();
  };

  const clearDay = useCallback(() => {
    setSelectedDay(null);
    setDayData(null);
    setDayError(null);
  }, []);

  // Clicking a bar (or its date label) pulls every attempt on that day. Clicking
  // the day that's already open closes it, so the same target toggles.
  const handleDayClick = useCallback(async (day) => {
    if (!day) return;
    if (day === selectedDay) {
      clearDay();
      return;
    }
    setSelectedDay(day);
    setDayData(null);
    setDayError(null);
    setDayLoading(true);
    try {
      const result = await fetchOtaAttemptsDay(day, startDate, endDate, addError);
      setDayData(result);
    } catch (err) {
      setDayError(err.message || `Failed to load attempts for ${day}.`);
    } finally {
      setDayLoading(false);
    }
  }, [selectedDay, clearDay, startDate, endDate, addError]);

  // Deep-link into the user's own analytics, windowed to the selected day. The
  // day boundaries are resolved in ANALYTICS_TIMEZONE and sent as absolute
  // instants (plus `tz`) so the target page shows the same window rather than
  // reading a bare wall time in the viewer's timezone.
  const getUserHref = useCallback((uid) => {
    if (!uid) return null;
    if (selectedDay) {
      const start = wallTimeToInstant(`${selectedDay}T00:00`, ANALYTICS_TIMEZONE);
      const end = wallTimeToInstant(`${selectedDay}T23:59`, ANALYTICS_TIMEZONE);
      if (start && end) {
        const params = new URLSearchParams({
          tab: 'analytics',
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          tz: ANALYTICS_TIMEZONE,
        });
        return `/users/${uid}?${params.toString()}`;
      }
    }
    return `/users/${uid}`;
  }, [selectedDay]);

  // A row with no uid has no user page to open
  const handleUserClick = useCallback((e, attempt) => {
    if (!attempt.user_id) e.preventDefault();
  }, []);

  const outcomes = useMemo(() => data?.outcomes || [], [data]);
  const chartData = useMemo(() => data?.dailyByOutcome || [], [data]);
  const colorMap = useMemo(() => buildColorMap(outcomes), [outcomes]);

  const successRate = data?.summary?.success_rate;
  const userSuccessRate = data?.summary?.user_success_rate;
  const hasRows = chartData.length > 0;

  return (
    <div className="ota-report">
      <div className="ota-header">
        <div className="ota-header-left">
          <div>
            <h1 className="ota-title">OTA Attempts</h1>
            <p className="ota-subtitle">
              Goggles firmware update attempts per day, split by outcome
            </p>
          </div>
        </div>
        <div className="ota-controls">
          <input
            className="ota-date-input"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <span className="ota-date-sep">to</span>
          <input
            className="ota-date-input"
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            className="ota-fetch-btn"
            onClick={fetchData}
            disabled={loading || !startDate || !endDate}
          >
            {loading ? 'Loading...' : 'Run Report'}
          </button>
        </div>
      </div>

      <AnalyticsTimezoneNote timezone={data?.timezone} />

      {error && <div className="ota-error">{error}</div>}

      {loading && (
        <div className="ota-loading">
          <div className="ota-spinner" />
          Querying BigQuery...
        </div>
      )}

      {!loading && !data && !error && (
        <div className="ota-empty">Select a date range and click Run Report.</div>
      )}

      {!loading && data && (
        <div className="ota-content">
          {/* Summary cards */}
          <div className="ota-summary">
            <div className="ota-card">
              <span className="ota-card-value">
                {data.summary.total_attempts.toLocaleString()}
              </span>
              <span className="ota-card-label">Total Attempts</span>
            </div>
            <div className="ota-card">
              <span className="ota-card-value" style={{ color: SUCCESS_COLOR }}>
                {data.summary.success_count.toLocaleString()}
              </span>
              <span className="ota-card-label">Succeeded</span>
            </div>
            <div className="ota-card">
              <span className="ota-card-value" style={{ color: FAILURE_RAMP[0] }}>
                {data.summary.failed_count.toLocaleString()}
              </span>
              <span className="ota-card-label">Failed</span>
            </div>
            <div className="ota-card">
              <span className="ota-card-value" style={{ color: NO_OUTCOME_COLOR }}>
                {data.summary.no_outcome_count.toLocaleString()}
              </span>
              <span className="ota-card-label">No Outcome</span>
            </div>
            <div className="ota-card">
              <span className="ota-card-value">
                {successRate == null ? '—' : `${(successRate * 100).toFixed(1)}%`}
              </span>
              <span className="ota-card-label">Success Rate (Attempts)</span>
            </div>
            <div className="ota-card">
              <span className="ota-card-value">
                {userSuccessRate == null ? '—' : `${(userSuccessRate * 100).toFixed(1)}%`}
              </span>
              <span className="ota-card-label">
                Success Rate (Users): {data.summary.users_succeeded.toLocaleString()} of {data.summary.total_unique_users.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Panel 1 — daily attempts, stacked by outcome */}
          <div className="ota-chart-section">
            <div className="ota-chart-header">
              <h3 className="ota-section-title">
                Daily OTA Attempts <span className="ota-section-sub">(stacked by outcome — failures split by error reason)</span>
              </h3>
              <div className="ota-legend">
                {outcomes.map(o => (
                  <span key={o.key} className="ota-legend-item">
                    <span className="ota-legend-swatch" style={{ background: colorMap[o.key] }} />
                    {o.label}
                  </span>
                ))}
              </div>
            </div>
            <div className="ota-chart-container">
              {hasRows ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                    <CartesianGrid stroke="#1f1f1f" vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="#444"
                      tick={({ x, y, payload }) => (
                        <g
                          transform={`translate(${x},${y})`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleDayClick(payload.value)}
                        >
                          <text
                            dy={12}
                            textAnchor="middle"
                            fill={selectedDay === payload.value ? '#fff' : '#999'}
                            fontSize={11}
                            fontWeight={selectedDay === payload.value ? 600 : 400}
                            fontFamily="SF Mono, Monaco, monospace"
                          >
                            {payload.value ? payload.value.slice(5) : ''}
                          </text>
                        </g>
                      )}
                    />
                    <YAxis
                      stroke="#444"
                      allowDecimals={false}
                      tick={{ fill: '#999', fontSize: 11, fontFamily: 'SF Mono, Monaco, monospace' }}
                      label={{ value: 'attempts', position: 'insideTopLeft', fill: '#666', fontSize: 10, dy: -10 }}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                      content={(props) => <ChartTooltip {...props} outcomes={outcomes} colorMap={colorMap} />}
                    />
                    {outcomes.map(o => (
                      <Bar
                        key={o.key}
                        dataKey={(row) => row.byOutcome?.[o.key]?.attempts || 0}
                        stackId="attempts"
                        name={o.label}
                        fill={colorMap[o.key]}
                        isAnimationActive={false}
                        cursor="pointer"
                        onClick={(barData) => handleDayClick(barData?.payload?.date || barData?.date)}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="ota-chart-empty">No OTA attempts in this date range.</div>
              )}
            </div>
            {hasRows && !selectedDay && (
              <div className="ota-chart-hint">Click a day to list every attempt on it.</div>
            )}
          </div>

          {/* Day drill-down — one row per attempt on the clicked day */}
          {selectedDay && (
            <div className="ota-chart-section ota-day-section">
              <div className="ota-chart-header">
                <h3 className="ota-section-title">
                  Attempts on <span className="ota-day-date">{selectedDay}</span>
                  {dayData && (
                    <span className="ota-section-sub">
                      {' '}
                      {dayData.totals.attempts.toLocaleString()} attempts ·{' '}
                      {dayData.totals.success.toLocaleString()} succeeded ·{' '}
                      {dayData.totals.failed.toLocaleString()} failed ·{' '}
                      {dayData.totals.no_outcome.toLocaleString()} no outcome ·{' '}
                      {dayData.totals.unique_users.toLocaleString()} users
                    </span>
                  )}
                </h3>
                <button className="ota-day-close" onClick={clearDay} title="Close day">&times;</button>
              </div>

              {dayError && <div className="ota-error">{dayError}</div>}

              {dayLoading && <div className="ota-day-loading">Loading attempts...</div>}

              {!dayLoading && !dayError && dayData && (
                dayData.attempts.length === 0 ? (
                  <div className="ota-day-empty">No OTA attempts recorded on this day.</div>
                ) : (
                  <div className="ota-table">
                    <div className="ota-table-header ota-table-row-grid">
                      <span>Time</span>
                      <span>User</span>
                      <span>Outcome</span>
                      <span>Firmware</span>
                      <span>Channel</span>
                      <span>Hardware</span>
                      <span>OS</span>
                      <span>App</span>
                      <span>Detail</span>
                      <span></span>
                    </div>
                    <div className="ota-table-body">
                      {dayData.attempts.map((a, i) => (
                        <a
                          key={`${a.event_time}-${a.user_identifier}-${i}`}
                          href={getUserHref(a.user_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`ota-table-row ota-table-row-grid ${a.user_id ? 'ota-table-row-clickable' : ''}`}
                          onClick={(e) => handleUserClick(e, a)}
                        >
                          <span className="ota-cell-mono">
                            {formatInTimeZone(a.event_time, dayData.timezone || ANALYTICS_TIMEZONE, {
                              hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
                            })}
                          </span>
                          <span className="ota-cell-user" title={a.user_id || a.user_pseudo_id || ''}>
                            {a.user_name || a.user_id || (a.user_pseudo_id ? `${a.user_pseudo_id.slice(0, 12)}...` : 'unknown')}
                          </span>
                          <span className="ota-cell-outcome">
                            <span
                              className="ota-outcome-badge"
                              style={{
                                '--outcome-color': colorMap[a.outcome_key]
                                  || (a.kind === 'success' ? SUCCESS_COLOR : MUTED_COLOR),
                              }}
                            >
                              {a.label}
                            </span>
                          </span>
                          {/* Goggles firmware the attempt was moving between. Builds that
                              don't report it read UNKNOWN, never a blank or a dash — the
                              value is missing from the event, not absent from the device. */}
                          <span className="ota-cell-mono ota-cell-fw">
                            {a.current_fw || a.target_fw ? (
                              <>
                                <Unknownable value={a.current_fw} />
                                <span className="ota-fw-arrow">→</span>
                                <span className="ota-fw-target"><Unknownable value={a.target_fw} /></span>
                              </>
                            ) : <span className="ota-unknown">UNKNOWN</span>}
                          </span>
                          <span className="ota-cell-muted"><Unknownable value={a.firmware_channel} /></span>
                          <span
                            className="ota-cell-mono ota-cell-muted"
                            title={a.hardware_model != null ? `hwVersionProto ${a.hardware_model}` : ''}
                          >
                            <Unknownable
                              value={a.hardware_variant || (a.hardware_model > 0 ? `hw ${a.hardware_model}` : null)}
                            />
                          </span>
                          <span className="ota-cell-muted"><Unknownable value={a.platform} /></span>
                          <span className="ota-cell-mono ota-cell-muted"><Unknownable value={a.app_version} /></span>
                          {/* The reason the drill-down exists: "Custom (unspecified)" and
                              "Download Failed" both hide the actual error in event params. */}
                          <span className="ota-cell-detail" title={a.detail || ''}>
                            {a.detail || '—'}
                          </span>
                          <span className="ota-copy-cell">
                            {a.user_id && (
                              <button
                                className="ota-copy-btn"
                                title="Copy user ID"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(a.user_id);
                                }}
                              >
                                &#x1F4CB;
                              </button>
                            )}
                          </span>
                        </a>
                      ))}
                    </div>
                    {dayData.truncated && (
                      <div className="ota-day-truncated">
                        Showing the first {dayData.attempts.length.toLocaleString()} attempts for this day.
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OtaAttemptsReport;
